import { z } from 'zod'

// Wire shapes for the task framework endpoints (docs/tasks/TASK_RUNNERS.md). This module is
// bundled into the frontends via @behuman/core-contract — only *.contract.ts files may
// cross the package boundary (web's fail-on-server-imports guard), so the status/log-level
// vocabulary is DEFINED here and re-exported by ../task-shared for server-side code.

export const TASK_STATUS_VALUES = ['active', 'disabled'] as const
export type TaskStatus = (typeof TASK_STATUS_VALUES)[number]

export const TASK_RUN_STATUS_VALUES = [
  'paused',
  'pending',
  'started',
  'cancel',
  'cancelled',
  'completed',
  'failed',
] as const
export type TaskRunStatus = (typeof TASK_RUN_STATUS_VALUES)[number]

export const TASK_LOG_LEVEL_VALUES = ['trace', 'debug', 'info', 'warn', 'error'] as const
export type TaskLogLevel = (typeof TASK_LOG_LEVEL_VALUES)[number]

/**
 * What happens to the followup items a run returns (see docs/tasks/TASK_RUNNERS.md):
 * 'run' = create child runs pending; 'pause' = create them paused; 'ignore' = create none
 * (items are still recorded on the run). task.followup_action is the default for the task's
 * runs; task_run.followup_action is what actually governs that run's followups.
 */
export const TASK_FOLLOWUP_ACTION_VALUES = ['run', 'pause', 'ignore'] as const
export type TaskFollowupAction = (typeof TASK_FOLLOWUP_ACTION_VALUES)[number]

/** Per-item outcome recorded in task_run.followup. */
export const TASK_FOLLOWUP_STATUS_VALUES = ['invalid', 'ignored', 'created'] as const
export type TaskFollowupStatus = (typeof TASK_FOLLOWUP_STATUS_VALUES)[number]

/** Admin actions on a run (POST /v2/tasks/{slug}/runs/{id}). */
export const TASK_RUN_ACTION_VALUES = ['cancel', 'pause', 'resume'] as const
export type TaskRunAction = (typeof TASK_RUN_ACTION_VALUES)[number]

/** One entry of task_run.followup — a runner-returned item annotated with its outcome. */
export const taskRunFollowupItemSchema = z.object({
  slug: z.string(),
  args: z.unknown().optional(),
  status: z.enum(TASK_FOLLOWUP_STATUS_VALUES),
  /** Filled when status = 'invalid': why the item could not be scheduled. */
  message: z.string().optional(),
})
export type TaskRunFollowupItem = z.infer<typeof taskRunFollowupItemSchema>

export const taskSchema = z.object({
  id: z.number(),
  slug: z.string(),
  name: z.string(),
  status: z.enum(TASK_STATUS_VALUES),
  logLevel: z.enum(TASK_LOG_LEVEL_VALUES),
  onSchedule: z.boolean(),
  schedule: z.string().nullable(),
  scheduledArgs: z.unknown(),
  maxConcurrentRuns: z.number(),
  maxInstanceRuns: z.number(),
  /** Default followup_action for this task's runs (scheduled, manual, chained). */
  followupAction: z.enum(TASK_FOLLOWUP_ACTION_VALUES),
  nextRunAt: z.iso.datetime().nullable(),
  heartbeatAt: z.iso.datetime().nullable(),
  /** Derived: an executor's heartbeat_at is < 2 minutes old. */
  runnerPresent: z.boolean(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  updatedBy: z.string().nullable(),
})

export const taskRunSchema = z.object({
  id: z.number(),
  taskId: z.number(),
  /** The owning task's slug (joined for convenience). */
  slug: z.string(),
  status: z.enum(TASK_RUN_STATUS_VALUES),
  args: z.unknown(),
  logLevel: z.enum(TASK_LOG_LEVEL_VALUES),
  runAt: z.iso.datetime(),
  actor: z.string().nullable(),
  startedAt: z.iso.datetime().nullable(),
  finishedAt: z.iso.datetime().nullable(),
  heartbeatAt: z.iso.datetime().nullable(),
  summary: z.string().nullable(),
  /** Governs this run's followups; defaulted from the task, overridable per run. */
  followupAction: z.enum(TASK_FOLLOWUP_ACTION_VALUES),
  /** Annotated followup items the runner returned; null until a successful completion. */
  followup: z.array(taskRunFollowupItemSchema).nullable(),
  /** Always null in list responses; populated by GET /v2/tasks/{slug}/runs/{id}. */
  log: z.string().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  updatedBy: z.string().nullable(),
})

export type Task = z.infer<typeof taskSchema>
export type TaskRun = z.infer<typeof taskRunSchema>
